// A49 — shared trigger for Bulk Print / Bulk Ship. Both now ALWAYS run as a server-side job (see
// ACTION_ITEMS.md A49): the browser never does the heavy work itself, only creates the job and polls
// it. One mechanism for every batch size — a small batch just finishes fast enough to feel instant
// (the toast auto-resolves and, for print, the PDF opens on its own); a large batch shows the same
// toast with a heads-up that it'll take longer, and the job keeps running server-side even if the
// user navigates away — it's always recoverable from the Bulk Actions page either way.
import React from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Hourglass } from 'lucide-react';
import { bulkActionsAPI } from '../services/api';

// This file is plain .js with no JSX, so the toast icons are built with createElement rather than
// pulling in the shared <Ico*> wrappers from components/common/Icons.jsx.
const ICO_WAIT  = React.createElement(Hourglass, { size: 17, color: '#64748b' });
const ICO_WARN  = React.createElement(AlertTriangle, { size: 17, color: '#f59e0b' });

const POLL_MS = 2000;
const RESOLVED_TOAST_MS = 6000; // explicit on every resolution call — the initial loading toast uses
// duration: Infinity, and react-hot-toast doesn't reliably reset that when a toast is later updated
// by the same id unless the new call states its own duration, which is what left the "bucket does
// not exist" error toast stuck open indefinitely instead of auto-dismissing.
export const LARGE_BATCH_THRESHOLD = 50; // messaging only — same code path either side of this number

// A job's stored `error` is the raw exception message from whatever failed server-side (a Cloud
// Storage SDK error, a courier API error, etc.) — useful for debugging, but sometimes a multi-line
// JSON dump that's unreadable as a toast. Keep it short here; the full value is still on the job
// itself, visible in the Bulk Actions detail popup for anyone who needs it.
const shortError = (msg) => {
  if (!msg) return 'Failed — check Bulk Actions for details';
  const oneLine = String(msg).replace(/\s+/g, ' ').trim();
  if (oneLine.length <= 80 && !oneLine.startsWith('{')) return oneLine;
  return 'Failed — see Bulk Actions for the full error';
};

// ── Poll lifecycle ─────────────────────────────────────────────────────────────
// Every timer scheduled here is tracked so it can actually be stopped. The previous version chained
// bare setTimeouts nothing held a handle to, and on a failed poll retried FOREVER:
//   .catch(() => setTimeout(tick, POLL_MS))   // no cap, no cancellation
// So a chain outlived the page that started it (navigating away from Orders did not stop it) and a
// persistently failing poll hammered the API for the life of the tab. Because the axios response
// interceptor also auto-retries GETs on 429, each tick costs TWO requests once the server starts
// pushing back — roughly 1.7 req/s against a per-session budget of 1000 per 15 minutes. A couple of
// wedged chains can exhaust the session's own rate limit, at which point every request the app makes
// fails and the dashboard looks dead until the user logs in fresh (a new token = a new bucket, which
// is why it appeared to "work in incognito"). Bounded + cancellable on both axes below.
const timers = new Set();
const schedule = (fn, ms) => {
  const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
  timers.add(id);
  return id;
};

// Call from a page's unmount cleanup. The server-side job is unaffected — it keeps running and stays
// recoverable from the Bulk Actions page; this only stops THIS tab from watching it.
export const stopAllBulkPolling = () => { timers.forEach(clearTimeout); timers.clear(); };

const MAX_CONSECUTIVE_POLL_FAILURES = 8; // ~10-16s of unbroken failure before we stop asking
const MAX_POLL_MS = 15 * 60 * 1000;      // absolute ceiling. A job still 'processing' after 10 min
// self-heals to 'failed' server-side (A49), so nothing legitimate outlives this — it exists purely
// so a chain can never become immortal.

// Poll one job to a terminal state. `onGaveUp(reason)` fires instead of `onSettled` when we stop
// watching without an answer — the job itself may well still be fine, so say so rather than
// reporting a failure that didn't happen.
const pollJob = (jobId, intervalMs, { onProcessing, onSettled, onGaveUp }) => {
  const startedAt = Date.now();
  const expired = () => Date.now() - startedAt > MAX_POLL_MS;
  let failures = 0;

  const tick = () => {
    bulkActionsAPI.get(jobId).then((r) => {
      failures = 0;
      const job = r.data.job;
      if (job.status !== 'processing') { onSettled(job); return; }
      if (expired()) { onGaveUp('taking longer than expected'); return; }
      onProcessing(job);
      schedule(tick, intervalMs);
    }).catch(() => {
      // A transient poll failure just retries — but only so many times. Giving up here does not
      // cancel anything server-side.
      if (++failures >= MAX_CONSECUTIVE_POLL_FAILURES || expired()) { onGaveUp('lost contact with the server'); return; }
      schedule(tick, intervalMs);
    });
  };

  schedule(tick, intervalMs);
};

export const runBulkAction = (type, orderIds, meta, { onDone } = {}) => {
  const isLarge = orderIds.length > LARGE_BATCH_THRESHOLD;
  const label = type === 'bulk_print' ? `${orderIds.length} label${orderIds.length > 1 ? 's' : ''}` : `${orderIds.length} shipment${orderIds.length > 1 ? 's' : ''}`;
  const toastId = `bulk-${Date.now()}`;
  toast.loading(
    isLarge ? `Preparing ${label}… this may take a bit — feel free to keep working, check Bulk Actions any time.`
            : `Preparing ${label}…`,
    { id: toastId, duration: Infinity }
  );

  return bulkActionsAPI.create(type, orderIds, meta)
    .then((res) => new Promise((resolve) => {
      pollJob(res.data.id, POLL_MS, {
        onProcessing: () => {},
        onGaveUp: (reason) => {
          toast(`Still working — ${reason}. Check Bulk Actions for the result.`, { id: toastId, icon: ICO_WAIT, duration: RESOLVED_TOAST_MS });
          resolve(null);
        },
        onSettled: (job) => {
          if (job.status === 'success' && type === 'bulk_print' && job.download_url) {
            toast.success('Labels ready — opening…', { id: toastId, duration: RESOLVED_TOAST_MS });
            window.open(job.download_url, '_blank');
          } else if (job.status === 'success') {
            toast.success('Done', { id: toastId, duration: RESOLVED_TOAST_MS });
          } else if (job.status === 'completed_with_errors' && type === 'bulk_print' && job.download_url) {
            // Some orders had no AWB yet (not booked/shipped) so got skipped — the PDF for the rest
            // is still real and usable, open it, just be honest about the shortfall instead of a bare
            // "Labels ready" that hides how many pages are actually in it.
            toast(`Opened ${job.summary?.ok ?? '?'} of ${job.summary?.total ?? '?'} labels — ${job.summary?.fail ?? 'some'} order(s) skipped (no AWB yet)`, { id: toastId, icon: ICO_WARN, duration: RESOLVED_TOAST_MS });
            window.open(job.download_url, '_blank');
          } else if (job.status === 'completed_with_errors') {
            toast(`Completed — ${job.summary?.fail || 'some'} order(s) failed`, { id: toastId, icon: ICO_WARN, duration: RESOLVED_TOAST_MS });
          } else {
            toast.error(shortError(job.error), { id: toastId, duration: RESOLVED_TOAST_MS });
          }
          if (onDone) onDone(job);
          resolve(job);
        },
      });
    }))
    .catch((err) => {
      toast.error(shortError(err.response?.data?.error) || 'Failed to start', { id: toastId, duration: RESOLVED_TOAST_MS });
      return null;
    });
};

const PROGRESS_POLL_MS = 1200;

// Same server-side job as runBulkAction above (nothing about how/where the work happens changes —
// still one background job per batch, still crash-proof at any size), but drives the OrdersPage
// progress-bar popup (shipProgress) instead of a toast — restoring the pre-A49 in-context UX users
// were used to for Bulk Print / Bulk Ship on that page specifically (reported regression: the toast
// replaced a popup with a live progress bar and a detailed result summary). `setProgress` is the
// page's setShipProgress; `onDone(job)` receives the finished job so the caller can build its own
// result popup (shipResult / printResult) from job.summary — this file has no opinion on that shape.
export const runBulkActionWithProgress = (type, orderIds, meta, { label, extra = {}, setProgress, onDone, hasResultPopup = false, onJobId, keepOnDone = false } = {}) => {
  const total = orderIds.length;
  // jobId is threaded through every progress payload so a caller tracking SEVERAL concurrent jobs
  // can tell them apart. Without it a second Bulk Ship overwrites the first one's progress — which
  // is exactly what happens when someone ships 100, minimises, then ships 50 more.
  let jobId = null;
  setProgress({ jobId, label, current: 0, total, ok: 0, fail: 0, done: false, ...extra });

  // `hasResultPopup`: the caller is about to open its own result modal, so clear this one immediately
  // instead of leaving the "done" progress modal stacked on top of it for 1.4s. Only a run with no
  // follow-up modal keeps the brief done-state flash.
  const finish = (patch, hasResultPopup = false) => {
    // `hasResultPopup` means the caller opens its own summary, so this one gets out of the way.
    // A caller that manages its own lifecycle (the bulk-progress provider, which may need to keep
    // a MINIMISED card on screen showing the finished state) passes `keepOnDone` and is left to
    // decide — the runner reports what happened and never decides how it is presented.
    if (keepOnDone) { setProgress({ jobId, label, total, done: true, ...extra, ...patch }); return; }
    if (hasResultPopup) { setProgress(null); return; }
    setProgress({ jobId, label, total, done: true, ...extra, ...patch });
    schedule(() => setProgress(null), 1400);
  };

  return bulkActionsAPI.create(type, orderIds, meta)
    .then((res) => new Promise((resolve) => {
      jobId = res.data.id;
      if (onJobId) onJobId(jobId);
      pollJob(res.data.id, PROGRESS_POLL_MS, {
        onProcessing: (job) => {
          const p = job.progress || {};
          // `detailIds` drives per-order locking: an order unlocks the moment IT is reported, not
          // when the whole batch ends, so a 500-order batch frees its rows continuously.
          const detailIds = ((job.summary && job.summary.details) || []).map(d => d.order_id);
          setProgress({ jobId, label, current: p.current ?? 0, total: p.total ?? total, ok: p.ok ?? 0, fail: p.fail ?? 0, done: false, detailIds, ...extra });
        },
        onGaveUp: (reason) => {
          finish({ current: 0, ok: 0, fail: 0, error: `Still working — ${reason}. Check Bulk Actions.` });
          resolve(null);
        },
        onSettled: (job) => {
          const s = job.summary || {};
          if (job.status === 'failed' && !job.summary) {
            // Whole job never produced a summary (e.g. "No shippable orders found") — nothing was
            // processed at all, not a partial failure. No result popup follows this, so let the
            // progress modal show the failure briefly.
            finish({ current: 0, ok: 0, fail: total, error: shortError(job.error) });
          } else {
            finish({ current: s.total ?? total, ok: s.ok ?? 0, fail: s.fail ?? 0 }, hasResultPopup);
          }
          if (onDone) onDone(job);
          resolve(job);
        },
      });
    }))
    .catch((err) => {
      finish({ current: 0, ok: 0, fail: total, error: shortError(err.response?.data?.error) });
      if (onDone) onDone({ status: 'failed', error: err.response?.data?.error || 'Failed to start' });
      return null;
    });
};
